import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import type { PersistQueryClientProviderProps } from '@tanstack/react-query-persist-client'
import { ApiError } from './errors'

export const ONE_HOUR_MS = 60 * 60 * 1000

/** Root of every managerium query key; only these are persisted. */
export const MANAGERIUM_QUERY_KEY = 'managerium'

let onUnauthorized: (() => void) | undefined

/** Auth registers what to do when the backend rejects the token (401). */
export const setUnauthorizedHandler = (handler: (() => void) | undefined) => {
  onUnauthorized = handler
}

const handleError = (error: unknown) => {
  if (error instanceof ApiError && error.status === 401) onUnauthorized?.()
}

/** Retry transient failures (network, 5xx) twice; a 4xx won't fix itself. */
export const shouldRetry = (failureCount: number, error: unknown): boolean =>
  failureCount < 2 && !(error instanceof ApiError && error.status < 500)

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleError }),
  mutationCache: new MutationCache({ onError: handleError }),
  defaultOptions: {
    // gcTime must cover the persist maxAge, or restored queries are evicted.
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      gcTime: ONE_HOUR_MS,
    },
    mutations: { retry: false },
  },
})

const persister = createSyncStoragePersister({
  storage: window.sessionStorage,
  key: 'lionfish-query-cache',
})

/** Survives a reload, not a closed tab. */
export const persistOptions: PersistQueryClientProviderProps['persistOptions'] =
  {
    persister,
    maxAge: ONE_HOUR_MS,
    dehydrateOptions: {
      shouldDehydrateQuery: query =>
        query.state.status === 'success' &&
        query.queryKey[0] === MANAGERIUM_QUERY_KEY,
    },
  }

/** Drops cached data, in memory and in storage, so the next user sees none of it. */
export const clearQueryCache = () => {
  queryClient.clear()
  persister.removeClient()
}
